import React, { useState, useEffect } from 'react';
import { Button, Input, Textarea, Card } from '../../components';
import * as pitchService from '../../services/pitch-service';

type PitchStatus = 'draft' | 'submitted' | 'approved' | 'revision_needed';

interface PitchForm {
  problem: string;
  solution: string;
  marketSize: string;
  businessModel: string;
  traction: string;
  team: string;
  financials: string;
  askAmount: string;
  useOfFunds: string;
  competitiveAdvantage: string;
}

const statusConfig: Record<PitchStatus, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'bg-surface text-muted' },
  submitted: { label: 'Submitted', color: 'bg-info/15 text-info' },
  approved: { label: 'Approved', color: 'bg-success/15 text-success' },
  revision_needed: { label: 'Revision Needed', color: 'bg-warning/15 text-warning' },
};

const PitchTemplate: React.FC = () => {
  const [status, setStatus] = useState<PitchStatus | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deckFile, setDeckFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState<PitchForm>({
    problem: '', solution: '', marketSize: '', businessModel: '',
    traction: '', team: '', financials: '', askAmount: '',
    useOfFunds: '', competitiveAdvantage: '',
  });

  useEffect(() => {
    pitchService.getMyPitches().then((pitches: any[]) => {
      if (pitches.length > 0) {
        const latest = pitches[0];
        setStatus(latest.status);
        setForm({
          problem: latest.problem || '', solution: latest.solution || '',
          marketSize: latest.marketSize || '', businessModel: latest.businessModel || '',
          traction: latest.traction || '', team: latest.team || '',
          financials: latest.financials || '', askAmount: latest.askAmount || '',
          useOfFunds: latest.useOfFunds || '', competitiveAdvantage: latest.competitiveAdvantage || '',
        });
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (field: keyof PitchForm, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) setDeckFile(e.target.files[0]);
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await pitchService.submitPitch({ ...form, status: 'draft' });
      setStatus('draft');
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleSubmit = async () => {
    if (!form.problem.trim() || !form.solution.trim()) return;
    setSubmitting(true);
    try {
      await pitchService.submitPitch({ ...form, status: 'submitted' });
      setStatus('submitted');
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  const isReadOnly = status === 'submitted' || status === 'approved';

  if (loading) return <div className="section-gap"><p className="text-muted">Loading...</p></div>;

  return (
    <div className="section-gap">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-text">Pitch Preparation</h1>
          <p className="text-muted mt-1">Build a structured pitch for your startup</p>
        </div>
        {status && (
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig[status].color}`}>
            {statusConfig[status].label}
          </span>
        )}
      </div>

      <Card>
        <h2 className="text-lg font-semibold text-text mb-5">Core Pitch Details</h2>
        <div className="space-y-5">
          <Textarea label="Problem" name="problem" placeholder="Describe the problem you are solving" value={form.problem} onChange={e => set('problem', e.target.value)} rows={3} disabled={isReadOnly} required />
          <Textarea label="Solution" name="solution" placeholder="How does your product/service solve this problem?" value={form.solution} onChange={e => set('solution', e.target.value)} rows={3} disabled={isReadOnly} required />
          <Input label="Market Size" name="marketSize" placeholder="e.g., $2B TAM, $500M SAM" value={form.marketSize} onChange={e => set('marketSize', e.target.value)} disabled={isReadOnly} />
          <Textarea label="Business Model" name="businessModel" placeholder="How do you make money?" value={form.businessModel} onChange={e => set('businessModel', e.target.value)} rows={3} disabled={isReadOnly} />
          <Textarea label="Traction" name="traction" placeholder="Key milestones, metrics, or achievements to date" value={form.traction} onChange={e => set('traction', e.target.value)} rows={3} disabled={isReadOnly} />
          <Textarea label="Team" name="team" placeholder="Describe your team and relevant experience" value={form.team} onChange={e => set('team', e.target.value)} rows={3} disabled={isReadOnly} />
        </div>
      </Card>

      <Card>
        <h2 className="text-lg font-semibold text-text mb-5">Financials & Ask</h2>
        <div className="space-y-5">
          <Textarea label="Financials" name="financials" placeholder="Revenue, burn rate, runway, projections" value={form.financials} onChange={e => set('financials', e.target.value)} rows={3} disabled={isReadOnly} />
          <Input label="Ask Amount" name="askAmount" placeholder="e.g., $500,000" value={form.askAmount} onChange={e => set('askAmount', e.target.value)} disabled={isReadOnly} />
          <Textarea label="Use of Funds" name="useOfFunds" placeholder="How will you allocate the investment?" value={form.useOfFunds} onChange={e => set('useOfFunds', e.target.value)} rows={3} disabled={isReadOnly} />
          <Textarea label="Competitive Advantage" name="competitiveAdvantage" placeholder="What is your moat? Why will you win?" value={form.competitiveAdvantage} onChange={e => set('competitiveAdvantage', e.target.value)} rows={3} disabled={isReadOnly} />
        </div>
      </Card>

      <Card>
        <h2 className="text-lg font-semibold text-text mb-4">Pitch Deck Upload</h2>
        <div className="border-2 border-dashed border-line rounded-card p-8 text-center">
          {deckFile ? (
            <div className="space-y-2">
              <svg className="w-8 h-8 text-chart mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm text-text font-medium">{deckFile.name}</p>
              <p className="text-xs text-muted">{(deckFile.size / 1024 / 1024).toFixed(2)} MB</p>
              {!isReadOnly && (
                <button className="text-sm text-chart hover:underline" onClick={() => setDeckFile(null)}>Remove</button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <svg className="w-10 h-10 text-muted mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-muted text-sm">Upload your pitch deck (PDF, PPT, PPTX)</p>
              <label className="inline-block">
                <input type="file" accept=".pdf,.ppt,.pptx" onChange={handleFileChange} className="hidden" disabled={isReadOnly} />
                <span className="px-4 py-2 bg-chart text-white rounded-lg text-sm font-medium cursor-pointer hover:opacity-90 transition-opacity">Choose File</span>
              </label>
            </div>
          )}
        </div>
      </Card>

      {!isReadOnly && (
        <div className="flex items-center gap-3 justify-end">
          <Button variant="ghost" onClick={handleSaveDraft} disabled={saving}>
            {saving ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !form.problem.trim()}>
            {submitting ? 'Submitting...' : 'Submit Pitch'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PitchTemplate;
